import { type FC, useEffect, useRef } from 'react';
import type { ConsoleOutputLine, GenerationStatus } from '../../types/generation';
import styles from './GenerateConsole.module.css';

interface GenerateConsoleProps {
  generating: boolean;
  consoleOutput: ConsoleOutputLine[];
  generationStatus: GenerationStatus | null;
  disabled?: boolean;
  title?: string;
  description?: string;
  runLabel?: string;
  stopLabel?: string;
  statusLabels?: Partial<Record<GenerationStatus, string>>;
  onGenerate: () => void;
  onStop: () => void;
}

const defaultStatusLabels: Record<GenerationStatus, string> = {
  running: 'Generation in progress...',
  success: 'Generation completed successfully',
  error: 'Generation failed',
  stopped: 'Stopped by user',
};

export const GenerateConsole: FC<GenerateConsoleProps> = ({
  generating,
  consoleOutput,
  generationStatus,
  disabled = false,
  title = 'Generate',
  description,
  runLabel = 'Generate',
  stopLabel = 'Stop',
  statusLabels,
  onGenerate,
  onStop,
}) => {
  const consoleRef = useRef<HTMLDivElement>(null);
  const hasConsole = generating || consoleOutput.length > 0;
  const labels = { ...defaultStatusLabels, ...statusLabels };

  // biome-ignore lint/correctness/useExhaustiveDependencies: consoleOutput.length triggers auto-scroll
  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [consoleOutput.length]);

  return (
    <div className={styles.generateSection}>
      <div className={styles.generateHeader} data-has-console={hasConsole}>
        <div>
          <h2 className={styles.generateTitle}>{title}</h2>
          {description && <p className={styles.generateDescription}>{description}</p>}
        </div>
        {generating ? (
          <button type="button" className={styles.generateButton} onClick={onStop}>
            {stopLabel}
          </button>
        ) : (
          <button type="button" className={styles.generateButton} onClick={onGenerate} disabled={disabled}>
            {runLabel}
          </button>
        )}
      </div>

      {hasConsole && (
        <div className={styles.console} ref={consoleRef}>
          {consoleOutput.map((line, index) => (
            <div key={index} className={styles.consoleLine}>
              {line.data}
            </div>
          ))}
          {generationStatus && (
            <div className={styles.consoleStatus} data-status={generationStatus}>
              {labels[generationStatus]}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
